import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "projects/ert/src/environments/environment";
import { Observable } from "rxjs";
import { IFireDepartmentBcTurnover } from "../../interfaces/firedepartment-bc-turnover/firedepartmentBcTurnover";
import { ICarryoverComments } from "../../interfaces/firedepartment-bc-turnover/firedepartmentCarryoverComments";
import { IPicklistValuesData } from "../../interfaces/administration/picklistValuesData";
import { IMultiCarryoverComments } from "../../interfaces/firedepartment-bc-turnover/multCarryoverComments";
import { FormGroup } from "@angular/forms";

@Injectable({
  providedIn: "root",
})
export class FireDepartmentsBcTurnoversService {
  private apiUrl = environment.apiUrl + "/fire-departments";
  constructor(private http: HttpClient) {}

  public getFireDepartmentsBcTurnovers(
    facilityId: Number
  ): Observable<IFireDepartmentBcTurnover[]> {
    return this.http.get<IFireDepartmentBcTurnover[]>(
      `${this.apiUrl}/${facilityId}/turnover`
    );
  }

  public getFireDepartmentsBcTurnoversById(
    recordId: number
  ): Observable<IFireDepartmentBcTurnover> {
    return this.http.get<IFireDepartmentBcTurnover>(
      `${this.apiUrl}/turnover/${recordId}`
    );
  }

  public getCarryoverComments(
    turnoverId: number
  ): Observable<ICarryoverComments[]> {
    return this.http.get<ICarryoverComments[]>(
      `${this.apiUrl}/turnover/${turnoverId}/carryover-comments`
    );
  }

  public createCarryoverComments(
    data: ICarryoverComments
  ): Observable<ICarryoverComments> {
    return this.http.post<ICarryoverComments>(
      `${this.apiUrl}/turnover/${data.turnoverId}/carryover-comments`,
      data
    );
  }

  public updateCarryoverComments(
    data: ICarryoverComments
  ): Observable<ICarryoverComments> {
    return this.http.put<ICarryoverComments>(
      `${this.apiUrl}/turnover/${data.turnoverId}/carryover-comments/`,
      data
    );
  }

  public multiUpdateCarryoverComments(
    data: IMultiCarryoverComments
  ): Observable<IFireDepartmentBcTurnover[]> {
    return this.http.post<IFireDepartmentBcTurnover[]>(
      `${this.apiUrl}/turnover/carryover-comments`,
      data
    );
  }

  public saveFireDepartmentBcTurnover(
    entity: IFireDepartmentBcTurnover
  ): Observable<IFireDepartmentBcTurnover> {
    if (entity.subCategory != null) {
      entity.subCategory = Number(entity.subCategory);
    }
    if (entity.status != null) {
      entity.status = Number(entity.status);
    }
    if (entity.pumpStatus != null) {
      entity.pumpStatus = Number(entity.pumpStatus);
    }
    return this.http.post<IFireDepartmentBcTurnover>(
      `${this.apiUrl}/turnover`,
      entity
    );
  }

  public updateBcTurnover(
    entity: Partial<IFireDepartmentBcTurnover>
  ): Observable<IFireDepartmentBcTurnover> {
    // Create a copy to avoid mutating the original object
    const updateData = { ...entity };

    // No need to convert status since it's already parsed in the component
    // Just validate we have the required fields for this update type
    if (!updateData.id) {
      console.error("Cannot update turnover: ID is required");
      throw new Error("ID is required for update operations");
    }

    // Ensure we're only sending the fields we should update
    const updatePayload = {
      id: updateData.id,
      initialComments: updateData.initialComments,
      resolvedComments: updateData.resolvedComments || "",
      status: updateData.status,
      resolvedDate: updateData.resolvedDate || null,
    };

    return this.http.put<IFireDepartmentBcTurnover>(
      `${this.apiUrl}/turnover`,
      updatePayload
    );
  }

  public deleteFireDepartmentBcTurnover(
    entity: IFireDepartmentBcTurnover
  ): Observable<boolean> {
    return this.http.delete<boolean>(`${this.apiUrl}/turnover/${entity.id}`);
  }

  public deleteCarryoverComments(
    turnoverId: number,
    carryoverId: number
  ): Observable<boolean> {
    return this.http.delete<boolean>(
      `${this.apiUrl}/turnover/${turnoverId}/carryover-comments/${carryoverId}`
    );
  }

  public getValueForField(
    picklistValues: IPicklistValuesData[],
    fieldId: number
  ) {
    let name = "";
    if (picklistValues) {
      const fieldValue = picklistValues.find(
        (x) => x.picklistFieldId === fieldId
      );
      if (fieldValue) {
        name = fieldValue.value!.toString();
      }
    }
    return name;
  }

  public validateBcTurnoverForm(turnoverForm: FormGroup<any>): string[] {
    const validationMessages: string[] = [];
    if (turnoverForm.get("date")?.errors) {
      validationMessages.push("Date is required");
    }
    if (turnoverForm.get("category")?.value === "-1") {
      validationMessages.push("Category is required");
    }
    if (turnoverForm.get("shift")?.value === "-1") {
      validationMessages.push("Shift is required");
    }
    if (turnoverForm.get("crew")?.value === "-1") {
      validationMessages.push("Crew is required");
    }
    if (turnoverForm.get("comments")?.value === "") {
      validationMessages.push("Comments are required.");
    }
    return validationMessages;
  }
}
